import axios, { AxiosInstance } from 'axios';

// Extend AxiosInstance to include download method
interface ApiInstance extends AxiosInstance {
    download: (path: string, filename?: string) => Promise<any>;
}

const api = axios.create({
    baseURL:process.env.REACT_APP_API_URL ?? "https://production.backend.juridicoja.com"
}) as ApiInstance;

const TOKEN_STORAGE_KEY = '@Jj:token';
const USER_STORAGE_KEY = '@Jj:user';

function isTokenInvalidResponse(message: unknown): boolean {
    if (typeof message !== 'string') return false;
    const m = message.toLowerCase();
    return (
        m.includes('token invalido') ||
        m.includes('token inválido') ||
        m.includes('token expirado') ||
        m.includes('acesso não autorizado') ||
        m.includes('acesso nao autorizado')
    );
}

function clearAuthAndRedirectToLogin(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    delete api.defaults.headers.common['Authorization'];
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
    }
}

api.interceptors.response.use(
    (response) => {
        const data = response?.data;
        if (data?.status === 'error' && isTokenInvalidResponse(data?.message)) {
            clearAuthAndRedirectToLogin();
            return Promise.reject(new Error(data.message));
        }
        return response;
    },
    (error) => {
        const message = error?.response?.data?.message ?? error?.message;
        if (error?.response?.status === 401 || error?.response?.status === 403 || isTokenInvalidResponse(message)) {
            clearAuthAndRedirectToLogin();
        }
        return Promise.reject(error);
    }
);

// Helper function to download files
api.download = async (path: string, filename?: string) => {
    try {
        const response = await api.get(path, {
            responseType: 'blob' // Important: tells axios to handle binary data
        });
        
        // Create a blob URL
        const blob = new Blob([response.data], { 
            type: response.headers['content-type'] || 'application/octet-stream' 
        });
        const url = window.URL.createObjectURL(blob);
        
        // Get filename from Content-Disposition header or use provided filename
        let downloadFilename = filename;
        if (!downloadFilename) {
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    downloadFilename = filenameMatch[1].replace(/['"]/g, '');
                }
            }
        }
        
        // If still no filename, extract from path
        if (!downloadFilename) {
            downloadFilename = path.split('/').pop() || 'download';
        }
        
        // Decode filename if it's URL encoded (e.g., %20 -> space)
        try {
            downloadFilename = decodeURIComponent(downloadFilename);
        } catch {
            // If decoding fails, use the original filename
        }
        
        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = downloadFilename;
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return response;
    } catch (error) {
        console.error('Download error:', error);
        throw error;
    }
};

export default api;
